import { createReducer } from "deox";
import jwt_decode from "jwt-decode";

import { userCheckComplete, userLoading, userSuccess } from "./authStateActions";
import { User } from "../../../types/types";
import { isUserRegistered } from "../../../utils/userUtils";
import { purge } from "../purge/purgeStateActions";

export interface AuthState {
  user: User | null;
  isLoadingUser: boolean;
  // True once the initial check for an already-signed-in session (via userManager.getUser()) has
  // resolved, whether or not it found a user. Lets routing wait instead of momentarily treating a
  // still-valid session (e.g. in a freshly opened tab) as logged out.
  hasCheckedUser: boolean;
  registeredUser: boolean;
  userId: string | null;
}

const initialState: AuthState = {
  user: null,
  isLoadingUser: false,
  hasCheckedUser: false,
  registeredUser: false,
  userId: null,
};

const authReducer = createReducer(initialState, (handleAction) => [
  handleAction(
    userSuccess,
    (state, action): AuthState => ({
      ...state,
      isLoadingUser: false,
      hasCheckedUser: true,
      registeredUser: isUserRegistered(action.payload),
      user: action.payload,
      userId: jwt_decode<string>(action.payload.access_token)["sub"].toString(),
    })
  ),
  handleAction(
    userLoading,
    (state): AuthState => ({
      ...state,
      registeredUser: false,
      isLoadingUser: true,
    })
  ),
  handleAction(
    userCheckComplete,
    (state): AuthState => ({
      ...state,
      // The bootstrap check is authoritative: if it found no usable session, any user carried
      // over from a stale rehydrated (sessionStorage) state must not be left signed in.
      user: null,
      registeredUser: false,
      userId: null,
      isLoadingUser: false,
      hasCheckedUser: true,
    })
  ),
  // A purge only ever happens after the initial session check has already run once, so it's safe
  // to keep hasCheckedUser true rather than re-showing the loading gate with nothing left to resolve it.
  handleAction(purge, (): AuthState => ({ ...initialState, hasCheckedUser: true })),
]);

export default authReducer;
